import React, { useState } from 'react';
import styles from './OnboardingModal.module.css';
import Button from './Button';
import { ONBOARDING_STEPS, ONBOARDING_GOALS, OnboardingStep } from '../constants/onboardingSteps';
import { addXP } from '../utils/questUtils';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: (selectedGoal: string) => void;
  onSkip: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onComplete, onSkip }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState<string>('');
  const [showGoalSelection, setShowGoalSelection] = useState(false);

  if (!isOpen) {
    return null;
  }

  const currentStep: OnboardingStep = ONBOARDING_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === ONBOARDING_STEPS.length - 1;
  const isGoalStep = currentStep.id === 'goal-setting';

  const handleNext = () => {
    if (isGoalStep && !showGoalSelection) {
      setShowGoalSelection(true);
      return;
    }

    if (isGoalStep && showGoalSelection && selectedGoal) {
      // Save the goal and continue
      localStorage.setItem('userGoal', selectedGoal);
      setShowGoalSelection(false);
      setCurrentStepIndex(currentStepIndex + 1);
      return;
    }

    if (isLastStep) {
      // Award XP for completing onboarding
      addXP(50);
      onComplete(selectedGoal || 'Track and reduce spending');
      return;
    }

    setCurrentStepIndex(currentStepIndex + 1);
  };

  const handlePrevious = () => {
    if (isGoalStep && showGoalSelection) {
      setShowGoalSelection(false);
      return;
    }

    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleGoalSelect = (goal: string) => {
    setSelectedGoal(goal);
  };

  const handleSkip = () => {
    // Set default goal if skipping
    localStorage.setItem('userGoal', 'Track and reduce spending');
    onSkip();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {/* Progress indicator */}
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ width: `${((currentStepIndex + 1) / ONBOARDING_STEPS.length) * 100}%` }}
            />
          </div>
          <span className={styles.progressText}>
            {currentStepIndex + 1} of {ONBOARDING_STEPS.length}
          </span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.emoji}>{currentStep.emoji}</div>
          <h2 className={styles.title}>{currentStep.title}</h2>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {!showGoalSelection ? (
            currentStep.content.map((paragraph, index) => (
              <p key={index} className={styles.paragraph}>
                {paragraph}
              </p>
            ))
          ) : (
            <div className={styles.goalSelection}>
              <p className={styles.goalPrompt}>Choose your primary financial goal:</p>
              <div className={styles.goalButtons}>
                {ONBOARDING_GOALS.map((goal) => (
                  <button
                    key={goal}
                    className={`${styles.goalButton} ${selectedGoal === goal ? styles.selected : ''}`}
                    onClick={() => handleGoalSelect(goal)}
                  >
                    {goal}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <div className={styles.leftActions}>
            {currentStep.skipEnabled && (
              <button className={styles.skipButton} onClick={handleSkip}>
                Skip Tutorial
              </button>
            )}
          </div>

          <div className={styles.rightActions}>
            {currentStepIndex > 0 && (
              <Button 
                onClick={handlePrevious}
                className={styles.secondaryButton}
              >
                Back
              </Button>
            )}
            
            <Button 
              onClick={handleNext}
              disabled={showGoalSelection && !selectedGoal}
              className={styles.primaryButton}
            >
              {isLastStep ? 'Start WealthPlay!' : (currentStep.actionButtonText || 'Next')}
            </Button>
          </div>
        </div>

        {/* Celebration for completion */}
        {isLastStep && (
          <div className={styles.celebration}>
            <div className={styles.celebrationEmoji}>🎉</div>
            <p className={styles.celebrationText}>
              You've earned <strong>50 XP</strong> for completing the onboarding!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingModal;